import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Eye, EyeOff, Copy, Pencil, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { PasswordInput } from "../../components/PasswordInput";

const ROLE_LABEL = {
  super_admin: "Super Admin", manager: "Manager", order_manager: "Order Manager",
  marketing_manager: "Marketing / CRM", kitchen_chef: "Kitchen Chef",
};

export default function AdminSecurity() {
  const [creds, setCreds] = useState([]);
  const [revealed, setRevealed] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ login_id: "", password: "" });
  const [editRow, setEditRow] = useState(null);

  const load = () => api.get("/admin/credentials").then((r) => setCreds(r.data));
  useEffect(() => { load(); }, []);

  const toggle = (id) => setRevealed((p) => ({ ...p, [id]: !p[id] }));

  const copy = async (c) => {
    const text = `Begum's Biryani Secrets — Login\nRole: ${ROLE_LABEL[c.role] || c.role}\nLogin ID: ${c.login_id}\nPassword: ${c.password || "(not stored)"}`;
    try { await navigator.clipboard.writeText(text); toast.success("Credentials copied"); }
    catch { toast.error("Copy failed"); }
  };

  const share = (c) => {
    const text = `Begum's Biryani Secrets — Login Credentials\nRole: ${ROLE_LABEL[c.role] || c.role}\nLogin ID: ${c.login_id}\nPassword: ${c.password || "(not stored)"}\n\nAdmin login: /admin/login${c.role === "kitchen_chef" ? "\nKitchen login: /kitchen/login" : ""}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const openEdit = (c) => { setEditRow(c); setForm({ login_id: c.login_id || "", password: "" }); setOpen(true); };
  const save = async () => {
    try {
      await api.put(`/admin/credentials/${editRow.id}`, { login_id: form.login_id, password: form.password || null });
      toast.success("Credentials updated"); setOpen(false); setEditRow(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-security">
      <div className="mb-6">
        <h1 className="font-serif text-3xl text-navy flex items-center gap-2"><KeyRound size={24} className="text-gold" /> Password & Security</h1>
        <p className="text-sm text-muted-foreground">All app logins — admin & kitchen chefs. View, edit, copy or share credentials.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg p-3 mb-5 flex items-center gap-2">
        <ShieldCheck size={15} /> These credentials are visible only to the Super Admin. Handle and share them responsibly.
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {creds.map((c) => (
          <div key={c.id} className="bg-white rounded-xl border border-black/5 p-4 shadow-sm" data-testid={`cred-${c.login_id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-navy">{c.name}</div>
                <span className="text-[11px] bg-navy text-gold px-2 py-0.5 rounded-full capitalize">{ROLE_LABEL[c.role] || c.role}</span>
              </div>
              <button onClick={() => openEdit(c)} data-testid={`cred-edit-${c.login_id}`} className="text-muted-foreground hover:text-navy"><Pencil size={16} /></button>
            </div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex items-center justify-between bg-beige rounded-lg px-3 py-2">
                <span className="text-xs text-muted-foreground">Login ID</span>
                <span className="font-medium text-navy" data-testid={`cred-loginid-${c.login_id}`}>{c.login_id}</span>
              </div>
              <div className="flex items-center justify-between bg-beige rounded-lg px-3 py-2">
                <span className="text-xs text-muted-foreground">Password</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-navy" data-testid={`cred-password-${c.login_id}`}>
                    {c.has_plain ? (revealed[c.id] ? c.password : "••••••••") : "not stored"}
                  </span>
                  {c.has_plain && (
                    <button onClick={() => toggle(c.id)} data-testid={`cred-reveal-${c.login_id}`} className="text-muted-foreground hover:text-navy">
                      {revealed[c.id] ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => copy(c)} data-testid={`cred-copy-${c.login_id}`} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-navy border border-navy/20 rounded-full py-2 hover:bg-beige"><Copy size={14} /> Copy</button>
              <button onClick={() => share(c)} data-testid={`cred-share-${c.login_id}`} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-white rounded-full py-2" style={{ backgroundColor: "#25D366" }}>WhatsApp</button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Edit Credentials</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-muted-foreground">Login ID (username)</label>
              <input value={form.login_id} onChange={(e) => setForm({ ...form, login_id: e.target.value })} data-testid="cred-edit-loginid" className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="text-xs text-muted-foreground">New Password (leave blank to keep)</label>
              <PasswordInput placeholder="New password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testId="cred-edit-password" className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2 text-sm" /></div>
            <button onClick={save} data-testid="cred-save-btn" className="w-full bg-navy text-cream font-semibold py-3 rounded-full hover:bg-navy-soft">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
